import { Grid } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import React, { useEffect, useState } from "react";
import {
  BooleanInput,
  SelectInput,
  required,
  useDataProvider,
} from "react-admin";
import { useForm } from "react-final-form";

interface IFixedLocationDaysOfWeekInput {
  isUseOneFixedLocation: boolean,
  allowSelectWave: boolean;
  schoolId: number;
}

interface ISelectOption {
  id: number;
  name: string;
}

interface IWaveSelectOption extends ISelectOption {
  isMonday: boolean;
  isTuesday: boolean;
  isWednesday: boolean;
  isThursday: boolean;
  isFriday: boolean;
}

const FixedLocationDaysOfWeekInput = ({
  isUseOneFixedLocation,
  allowSelectWave,
  schoolId,
}: IFixedLocationDaysOfWeekInput) => {
  const [isMonday, setIsMonday] = useState<boolean>(false);
  const [isTuesday, setIsTuesday] = useState<boolean>(false);
  const [isWednesday, setIsWednesday] = useState<boolean>(false);
  const [isThursday, setIsThursday] = useState<boolean>(false);
  const [isFriday, setIsFriday] = useState<boolean>(false);

  const [fixLocationOptions, setFixLocationOptions] = useState<ISelectOption[]>(
    []
  );
  const [waveOptions, setWaveOptions] = useState<IWaveSelectOption[]>([]);

  const provider = useDataProvider();
  const formValues = useForm().getState().values;

  useEffect(() => {
    setIsMonday(formValues.isMonday);
    setIsTuesday(formValues.isTuesday);
    setIsWednesday(formValues.isWednesday);
    setIsThursday(formValues.isThursday);
    setIsFriday(formValues.isFriday);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.isNotTaking]);

  useEffect(() => {
    provider
      .getList("ccaFixedLocationDropOffs", {
        filter: { SchoolId: schoolId, Status: "active" },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        const fixLocations = data as ICcaFixedLocationDropOff[];
        const options = fixLocations.map((location) => ({
          id: location.id,
          name: location.name,
        }));
        setFixLocationOptions(options);
      });

    provider
      .getList("busSchedules", {
        filter: { SchoolId: schoolId, Type: "cca" },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        const waves = data as IBusSchedule[];
        const options = waves.map((wave) => ({
          id: wave.id,
          name: wave.name,
          isMonday: wave.isMonday,
          isTuesday: wave.isTuesday,
          isWednesday: wave.isWednesday,
          isThursday: wave.isThursday,
          isFriday: wave.isFriday,
        }));
        setWaveOptions(options);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  return (
    <>
      <Grid item xs={2}>
        <BooleanInput
          label="Monday"
          source="isMonday"
          defaultValue={false}
          onChange={(e: boolean) => {
            setIsMonday(e);
          }}
        />
      </Grid>
      {isMonday && (
        <SanitizedGrid
          container
          spacing={2}
          style={{ margin: "-20px 0px 20px" }}
        >
          {!isUseOneFixedLocation && (
            <Grid item xs={12}>
              <SelectInput
                source="fixedLocationMondayId"
                label="Select Fixed Location Monday"
                choices={fixLocationOptions}
                validate={required()}
              />
            </Grid>
          )}
          {allowSelectWave && (
            <Grid item xs={12}>
              <SelectInput
                source="waveMondayId"
                label="Select Wave Monday"
                choices={waveOptions.filter((wave) => wave.isMonday)}
                validate={required()}
              />
            </Grid>
          )}
        </SanitizedGrid>
      )}

      <Grid item xs={2}>
        <BooleanInput
          label="Tuesday"
          source="isTuesday"
          defaultValue={false}
          onChange={(e: boolean) => {
            setIsTuesday(e);
          }}
        />
      </Grid>
      {isTuesday && (
        <SanitizedGrid
          container
          spacing={2}
          style={{ margin: "-20px 0px 20px" }}
        >
          {!isUseOneFixedLocation && (
            <Grid item xs={12}>
              <SelectInput
                source="fixedLocationTuesdayId"
                label="Select Fixed Location Tuesday"
                choices={fixLocationOptions}
                validate={required()}
              />
            </Grid>
          )}
          {allowSelectWave && (
            <Grid item xs={12}>
              <SelectInput
                source="waveTuesdayId"
                label="Select Wave Tuesday"
                choices={waveOptions.filter((wave) => wave.isTuesday)}
                validate={required()}
              />
            </Grid>
          )}
        </SanitizedGrid>
      )}

      <Grid item xs={2}>
        <BooleanInput
          label="Wednesday"
          source="isWednesday"
          defaultValue={false}
          onChange={(e: boolean) => {
            setIsWednesday(e);
          }}
        />
      </Grid>
      {isWednesday && (
        <SanitizedGrid
          container
          spacing={2}
          style={{ margin: "-20px 0px 20px" }}
        >
          {!isUseOneFixedLocation && (
            <Grid item xs={12}>
              <SelectInput
                source="fixedLocationWednesdayId"
                label="Select Fixed Location Wednesday"
                choices={fixLocationOptions}
                validate={required()}
              />
            </Grid>
          )}
          {allowSelectWave && (
            <Grid item xs={12}>
              <SelectInput
                source="waveWednesdayId"
                label="Select Wave Wednesday"
                choices={waveOptions.filter((wave) => wave.isWednesday)}
                validate={required()}
              />
            </Grid>
          )}
        </SanitizedGrid>
      )}

      <Grid item xs={2}>
        <BooleanInput
          label="Thursday"
          source="isThursday"
          defaultValue={false}
          onChange={(e: boolean) => {
            setIsThursday(e);
          }}
        />
      </Grid>
      {isThursday && (
        <SanitizedGrid
          container
          spacing={2}
          style={{ margin: "-20px 0px 20px" }}
        >
          {!isUseOneFixedLocation && (
            <Grid item xs={12}>
              <SelectInput
                source="fixedLocationThursdayId"
                label="Select Fixed Location Thursday"
                choices={fixLocationOptions}
                validate={required()}
              />
            </Grid>
          )}
          {allowSelectWave && (
            <Grid item xs={12}>
              <SelectInput
                source="waveThursdayId"
                label="Select Wave Thursday"
                choices={waveOptions.filter((wave) => wave.isThursday)}
                validate={required()}
              />
            </Grid>
          )}
        </SanitizedGrid>
      )}

      <Grid item xs={2}>
        <BooleanInput
          label="Friday"
          source="isFriday"
          defaultValue={false}
          onChange={(e: boolean) => {
            setIsFriday(e);
          }}
        />
      </Grid>
      {isFriday && (
        <SanitizedGrid
          container
          spacing={2}
          style={{ margin: "-20px 0px 20px" }}
        >
          {!isUseOneFixedLocation && (
            <Grid item xs={12}>
              <SelectInput
                source="fixedLocationFridayId"
                label="Select Fixed Location Friday"
                choices={fixLocationOptions}
                validate={required()}
              />
            </Grid>
          )}
          {allowSelectWave && (
            <Grid item xs={12}>
              <SelectInput
                source="waveFridayId"
                label="Select Wave Friday"
                choices={waveOptions.filter((wave) => wave.isFriday)}
                validate={required()}
              />
            </Grid>
          )}
        </SanitizedGrid>
      )}
    </>
  );
};

export default FixedLocationDaysOfWeekInput;
